let config = {};

function ensureTrailingSlash(value) {
  return value.endsWith("/") ? value : `${value}/`;
}

function normalizePathBase(value = "/") {
  let trimmed = `${value || "/"}`.trim();

  try {
    trimmed = new URL(trimmed).pathname || "/";
  } catch {
    // Keep relative path-style values as-is.
  }

  if (!trimmed || trimmed === "/") {
    return "/";
  }

  const normalized = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  return ensureTrailingSlash(normalized.replace(/\/+$/, ""));
}

function joinUrl(base, routePath = "/") {
  const normalizedRoute = routePath.startsWith("/")
    ? routePath.slice(1)
    : routePath;

  try {
    return new URL(normalizedRoute, ensureTrailingSlash(base)).toString();
  } catch {
    const normalizedBase = normalizePathBase(base);
    return `${normalizedBase}${normalizedRoute}`;
  }
}

function toAbsoluteUrl(value) {
  try {
    return new URL(value).toString();
  } catch {
    return null;
  }
}

function removeTrailingSlash(value) {
  return String(value || "").replace(/\/+$/, "");
}

export async function loadConfig() {
  const baseUrl = (process.env.BASE_URL || "/").replace(/\/+$/, "");

  const candidates = [`${baseUrl}/config.json`, "/config.json"];

  try {
    let loaded = null;

    for (const path of candidates) {
      const response = await fetch(path);

      if (response.ok) {
        loaded = await response.json();
        break;
      }
    }

    if (!loaded) {
      throw new Error("Failed to load config.json");
    }

    config = loaded;

    console.log("Runtime config loaded:", config);
  } catch (error) {
    console.error("Error loading config.json:", error);
  }
}

export function getConfig(key) {
  if (!key) return config;

  return key.split(".").reduce((obj, part) => {
    return obj && obj[part] !== undefined ? obj[part] : undefined;
  }, config);
}

export function getAppBaseUrl() {
  return normalizePathBase(getConfig("APP_BASE_URL") || "/");
}

export function getPublicOrigin() {
  const configured = getConfig("PUBLIC_ORIGIN");

  if (configured) {
    const absolute = toAbsoluteUrl(configured);
    if (absolute) {
      return removeTrailingSlash(absolute);
    }
  }

  if (typeof window !== "undefined") {
    return window.location.origin;
  }

  return "";
}

export function getOauthBaseUrl() {
  const configured = getConfig("OAUTH.BASE_URL");

  if (configured) {
    const absolute = toAbsoluteUrl(configured);
    if (absolute) {
      return removeTrailingSlash(absolute);
    }

    const normalizedPath = normalizePathBase(configured);
    return removeTrailingSlash(normalizedPath);
  }

  return "";
}

export function buildOauthUrl(routePath = "/") {
  const configuredOauthBase = getOauthBaseUrl();
  const oauthBase = configuredOauthBase || "";

  if (!oauthBase) {
    return routePath;
  }

  return `${oauthBase}/${String(routePath || "").replace(/^\/oauth2\/?/, "")}`;
}

export function getRouterBase() {
  return getAppBaseUrl();
}

export function buildAppUrl(routePath = "/") {
  const appBase = getAppBaseUrl();
  const publicOrigin = getPublicOrigin();

  if (publicOrigin) {
    return joinUrl(publicOrigin + appBase, routePath);
  }

  return joinUrl(appBase, routePath);
}

export function getAuthCheckUrl() {
  return buildOauthUrl(getConfig("OAUTH.AUTH_CHECK") || "/oauth2/auth");
}

export function buildLoginUrl(routePath = "/") {
  const loginPath = getConfig("OAUTH.LOGIN") || "/oauth2/sign_in";
  const returnTarget = buildAppUrl(routePath);
  const loginTarget = buildOauthUrl(loginPath);

  return `${loginTarget}?rd=${encodeURIComponent(returnTarget)}`;
}

export function buildLogoutUrl() {
  return buildOauthUrl(getConfig("OAUTH.LOGOUT") || "/oauth2/sign_out");
}
